import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X, CheckCircle, Play, Mail, Phone, Facebook, Quote, ChevronLeft, ChevronRight, Code, Palette, Bot, Video, Globe, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logos
const LOGO_PNG = "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/q2693oj8_rsz_untitled_design_5-1-Photoroom.png";
const FOOTER_LOGO = "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/d7x4trzz_Untitled-design-8-1.webp";

// WhatsApp
const WHATSAPP_NUMBER = "+8801346133117";

// Services Data with Images
const SERVICES = [
  {
    id: 'web-development',
    title: 'Website Development',
    subtitle: 'Build your digital presence',
    icon: Globe,
    image: 'https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/47qg1g1h_rsz_1chatgpt_image_dec_26_2025_04_17_01_pm-1-672x448.webp',
    features: [
      'Stunning, conversion-focused designs',
      'Mobile-first responsive layouts',
      'Lightning-fast performance',
      'SEO optimized & secure'
    ],
    color: 'from-sky-500 to-indigo-500'
  },
  {
    id: 'web-software',
    title: 'Custom Web Software',
    subtitle: 'Powerful solutions for your business',
    icon: Code,
    image: 'https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/543pnmj4_rsz_1chatgpt_image_dec_26_2025_04_13_16_pm-1-672x448.webp',
    features: [
      'Custom dashboards & portals',
      'Workflow automation systems',
      'Secure data management',
      'Real-time analytics & reports'
    ],
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'branding',
    title: 'Digital Branding',
    subtitle: 'Stand out from the crowd',
    icon: Palette,
    image: 'https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/6iuzborp_rsz_1chatgpt_image_dec_26_2025_04_08_02_pm-1-672x448.webp',
    features: [
      'Complete brand identity design',
      'Social media creative assets',
      'Consistent visual language',
      'Premium & professional look'
    ],
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'ai-automation',
    title: 'AI Automation',
    subtitle: 'Work smarter, not harder',
    icon: Bot,
    image: 'https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/aqkspt3r_rsz_18929.jpg',
    features: [
      'AI chatbots & virtual assistants',
      'Automated customer support',
      'Smart lead generation',
      'CRM & workflow integrations'
    ],
    color: 'from-emerald-500 to-teal-500'
  },
  {
    id: 'content-marketing',
    title: 'Content Marketing',
    subtitle: 'Grow your audience organically',
    icon: Video,
    image: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=800',
    features: [
      'Engaging video content',
      'Strategic content planning',
      'Social media management',
      'Brand storytelling that converts'
    ],
    color: 'from-orange-500 to-red-500'
  }
];

// Testimonials Data
const TESTIMONIALS = [
  {
    id: 1,
    text: "The exam portal and booking system developed by VirtualSwift significantly reduced our manual workload. Students can now book exams, access portals, and manage schedules smoothly. Reliable support and professional execution. Highly recommended.",
    name: "Yasir Rahman",
    role: "CEO",
    image: "https://virtualswift.com/wp-content/uploads/2025/12/Yasir-1.jpg"
  },
  {
    id: 2,
    text: "Clean, responsive, and professional front-end development delivered exactly as required.",
    name: "Apricot IT",
    role: "UK",
    image: null
  }
];

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img src={LOGO_PNG} alt="VirtualSwift" className="h-12 w-auto" />
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className={`font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-sky-500' : 'text-slate-700 hover:text-sky-500'}`}>
              Services
            </button>
            <button onClick={() => scrollToSection('about')} className={`font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-sky-500' : 'text-slate-700 hover:text-sky-500'}`}>
              About
            </button>
            <Link to="/cd-ielts" className={`font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-sky-500' : 'text-slate-700 hover:text-sky-500'}`}>
              CD IELTS
            </Link>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full px-6"
            >
              Contact Us
            </Button>
          </nav>
          
          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 bg-white rounded-lg shadow-lg mt-2">
            <div className="flex flex-col gap-2 px-4">
              <button onClick={() => scrollToSection('services')} className="text-left text-slate-600 py-3 border-b border-slate-100">Services</button>
              <button onClick={() => scrollToSection('about')} className="text-left text-slate-600 py-3 border-b border-slate-100">About</button>
              <Link to="/cd-ielts" className="text-left text-slate-600 py-3 border-b border-slate-100">CD IELTS</Link>
              <Link to="/booking" className="block mt-2">
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full">
                  Book a Call
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Contact Modal Component
const ContactModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        package_interest: 'homepage-quote'
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    onOpenChange(val);
    if (!val) setSubmitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="contact-modal" className="max-w-lg w-[92vw] sm:w-full bg-white border-0 rounded-3xl shadow-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto my-4">
        {submitted ? (
          <div data-testid="modal-success-message" className="text-center py-12 px-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-9 h-9 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Message Sent!</h3>
            <p className="text-slate-500 mt-2">We will get back to you shortly.</p>
            <Button
              onClick={() => handleClose(false)}
              className="mt-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full px-10 py-5 shadow-lg shadow-blue-500/20"
            >
              Close
            </Button>
          </div>
        ) : (
        <>
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 sm:px-8 text-center">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white">Get a Free Quote</h3>
          <p className="text-blue-100 text-sm mt-1">Tell us about your project and we'll get back to you shortly.</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Name</Label>
            <Input
              data-testid="modal-name-input"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Email</Label>
              <Input
                data-testid="modal-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@email.com"
                className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Phone</Label>
              <Input
                data-testid="modal-phone-input"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+880 1XXX-XXXXXX"
                className="h-12 rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Message</Label>
            <Textarea
              data-testid="modal-message-input"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your project..."
              rows={3}
              className="rounded-xl border-slate-200 focus:border-sky-500 focus:ring-sky-500/20"
            />
          </div>
          <Button
            data-testid="modal-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl py-6 text-base font-semibold shadow-lg shadow-blue-500/20 transition-all hover:shadow-xl"
          >
            {loading ? 'Sending...' : 'Send Message'}
            {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Hero Section
const HeroSection = ({ onOpenContactModal }) => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 hero-mesh pointer-events-none"></div>
      <div className="absolute top-20 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-sky-400/10 to-transparent rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-3xl pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 rounded-full border border-sky-100 mb-8">
              <span className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-sky-700">Everything Your Business Needs</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Future-Proof Your Brand with
              <span className="block text-gradient">Web, Ads, Design & AI Automation</span>
            </h1>
            
            <p className="font-lato text-lg md:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              VirtualSwift is a full-stack digital agency providing Web Development, Ad Tech, Branding & Design, Social Media Marketing, Video Marketing — plus AI Agents & Business Automation to scale faster with less manual work.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                data-testid="hero-get-quote-btn"
                onClick={onOpenContactModal}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl transition-all hover:scale-105"
              >
                Get a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                data-testid="hero-services-btn"
                variant="outline"
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="rounded-full px-10 py-6 text-lg border-slate-200 hover:border-sky-200 hover:bg-sky-50 hover:text-black"
              >
                Our Services
              </Button>
            </div>
            
            <div className="flex items-center justify-center lg:justify-start gap-8 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">6+</div>
                <div className="text-sm text-slate-500">Years Experience</div>
              </div>
              <div className="w-px h-12 bg-slate-200"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">100%</div>
                <div className="text-sm text-slate-500">Client Satisfaction</div>
              </div>
            </div>
          </div>
          
          {/* Video Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/20 to-purple-500/20 rounded-3xl blur-2xl"></div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              {/* Video Thumbnail with Play Button */}
              <div className="relative aspect-video bg-slate-900 group cursor-pointer" onClick={() => setVideoModalOpen(true)}>
                <img 
                  src="https://img.youtube.com/vi/iPJx1T9e59s/maxresdefault.jpg"
                  alt="VirtualSwift Video"
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-70 transition-opacity"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-sky-500 hover:bg-sky-400 rounded-full flex items-center justify-center shadow-lg shadow-sky-500/40 group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-6">
                  <p className="text-white font-semibold">Watch Our Story</p>
                  <p className="text-slate-300 text-sm">See how we help businesses grow</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="max-w-4xl p-0 bg-slate-900/95 border-slate-700">
          <div className="relative">
            <div className="p-4 pt-10">
              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                  src={videoModalOpen ? "https://www.youtube.com/embed/iPJx1T9e59s?autoplay=1" : ""}
                  title="VirtualSwift Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Everything <span className="text-gradient">You Need</span>
          </h2>
          <p className="font-lato text-lg text-slate-600 max-w-2xl mx-auto">
            Stop juggling multiple vendors. VirtualSwift delivers complete, result-focused digital solutions designed to convert, scale, and perform.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.slice(0, 4).map((service) => (
            <div 
              key={service.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100 hover:border-sky-200 hover:-translate-y-2"
            >
              {/* Service Image */}
              <div className="relative h-52 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
                <div className={`absolute top-4 right-4 w-10 h-10 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
                  <service.icon className="w-5 h-5 text-white" />
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white font-bold text-lg mb-1">{service.title}</h3>
                  <p className="text-white/70 text-sm">{service.subtitle}</p>
                </div>
              </div>
              
              {/* Service Content */}
              <div className="p-5">
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-600">
                      <div className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-3 h-3 text-sky-500" />
                      </div>
                      <span className="font-lato text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        
        {/* Fifth Service - Full Width */}
        <div className="mt-8 max-w-md mx-auto">
          <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100 hover:border-sky-200 hover:-translate-y-2">
            <div className="relative h-52 overflow-hidden">
              <img 
                src={SERVICES[4].image} 
                alt={SERVICES[4].title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
              <div className={`absolute top-4 right-4 w-10 h-10 rounded-xl bg-gradient-to-br ${SERVICES[4].color} flex items-center justify-center shadow-lg`}>
                <Video className="w-5 h-5 text-white" />
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-white font-bold text-lg mb-1">{SERVICES[4].title}</h3>
                <p className="text-white/70 text-sm">{SERVICES[4].subtitle}</p>
              </div>
            </div>
            <div className="p-5">
              <ul className="space-y-3">
                {SERVICES[4].features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-600">
                    <div className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-sky-500" />
                    </div>
                    <span className="text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 lg:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Video Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/20 to-purple-500/20 rounded-3xl blur-2xl"></div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/iPJx1T9e59s"
                  title="VirtualSwift - You Dream We Build"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
          
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-6">
              ABOUT OUR COMPANY
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              You Dream — <span className="text-gradient">We Build</span>
            </h2>
            <p className="font-lato text-slate-600 mb-6 leading-relaxed">
              VirtualSwift was built to solve one problem: fragmented digital growth. Instead of selling isolated services, we create complete digital ecosystems—where your website, marketing, content, and automation work together to generate real business outcomes.
            </p>
            
            <div className="flex items-center gap-6 mb-8 p-4 bg-white rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-sky-500">6+</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">We have more than 6+ year of</h4>
                <p className="text-slate-500">services experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full px-6">
                DISCOVER MORE
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <a href="tel:01346133117" className="flex items-center gap-3 text-slate-700 hover:text-sky-500 transition-colors">
                <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Call Anytime</p>
                  <p className="font-semibold">01346133117</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Most Trusted Agency Section
const TrustedAgencySection = () => {
  return (
    <section className="relative py-24 bg-slate-900 overflow-hidden">
      {/* Background Image/Video */}
      <div className="absolute inset-0">
        <img 
          src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Team working"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Play Button */}
        <button className="w-20 h-20 bg-sky-500 hover:bg-sky-400 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg shadow-sky-500/30 transition-all hover:scale-110">
          <Play className="w-8 h-8 text-white ml-1" />
        </button>
        
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
          Most Trusted Agency
        </h2>
        
        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-sky-500 rounded-2xl p-6 text-left">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">Professional Staff</h3>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 text-left">
            <div className="flex items-center justify-between">
              <h3 className="text-slate-900 font-semibold">We Satisfy, You 100%</h3>
              <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-sky-500" />
              </div>
            </div>
          </div>
          
          <div className="bg-orange-500 rounded-2xl p-6 text-left">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">High Quality Designing</h3>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Palette className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  return (
    <section id="testimonials" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-6">
            Testimonials
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            What They're Talking About Us?
          </h2>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-slate-50 rounded-3xl p-8 md:p-12">
            <Quote className="w-12 h-12 text-sky-500/30 absolute top-8 left-8" />
            
            <div className="text-center pt-8">
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8">
                "{TESTIMONIALS[currentIndex].text}"
              </p>
              
              <div className="flex items-center justify-center gap-4">
                {TESTIMONIALS[currentIndex].image && (
                  <img 
                    src={TESTIMONIALS[currentIndex].image} 
                    alt={TESTIMONIALS[currentIndex].name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                )}
                <div className="text-left">
                  <h4 className="font-semibold text-slate-900">{TESTIMONIALS[currentIndex].name}</h4>
                  <p className="text-slate-500 text-sm">{TESTIMONIALS[currentIndex].role}</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center gap-4 mt-8">
              <button 
                onClick={prevTestimonial}
                className="p-3 bg-white hover:bg-slate-100 rounded-full transition-colors shadow-sm"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <button 
                onClick={nextTestimonial}
                className="p-3 bg-white hover:bg-slate-100 rounded-full transition-colors shadow-sm"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section (Why Choose Us Style)
const ContactSection = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        package_interest: 'general-inquiry'
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Why Choose Us */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-500/20 text-sky-300 rounded-full text-sm font-medium mb-6">
              WHY CHOOSE US
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Recognize
            </h2>
            
            <div className="relative mt-12">
              {/* Decorative Arrow */}
              <div className="hidden lg:block absolute -right-20 top-0">
                <svg className="w-32 h-32 text-orange-500 opacity-50" viewBox="0 0 100 100">
                  <path d="M10,50 Q50,10 90,50" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5"/>
                  <polygon points="85,45 95,50 85,55" fill="currentColor"/>
                </svg>
              </div>
              
              <a href="mailto:info@virtualswift.com" className="flex items-center gap-4 text-white hover:text-sky-400 transition-colors">
                <div className="w-14 h-14 bg-sky-500 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6" />
                </div>
                <span className="text-lg">info@virtualswift.com</span>
              </a>
            </div>
          </div>
          
          {/* Right Side - Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            {submitted && (
              <div data-testid="contact-success-message" className="mb-6 bg-green-50 border border-green-200 rounded-xl p-5 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <h4 className="font-semibold text-green-800 text-lg">Message Sent Successfully!</h4>
                <p className="text-green-600 text-sm mt-1">We will get back to you soon.</p>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="mt-3 text-sm text-sky-600 hover:text-sky-700 underline"
                >
                  Send another message
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Input 
                  data-testid="contact-name-input"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Name"
                  className="border-slate-200 focus:border-sky-500"
                />
              </div>
              <div>
                <Input 
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="E-Mail"
                  className="border-slate-200 focus:border-sky-500"
                />
              </div>
              <div>
                <Input 
                  data-testid="contact-phone-input"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Phone Number"
                  className="border-slate-200 focus:border-sky-500"
                />
              </div>
              <div>
                <Textarea 
                  data-testid="contact-message-input"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Message"
                  rows={4}
                  className="border-slate-200 focus:border-sky-500"
                />
              </div>
              <Button 
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-6"
              >
                {loading ? 'Sending...' : 'Send'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src={FOOTER_LOGO} alt="VirtualSwift" className="h-14 mb-4" />
            <p className="font-lato text-slate-400 max-w-md">
              VirtualSwift is a full-stack digital agency providing complete digital solutions designed to convert, scale, and perform.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#services" className="hover:text-sky-400 transition-colors">Web Development</a></li>
              <li><a href="#services" className="hover:text-sky-400 transition-colors">Digital Branding</a></li>
              <li><a href="#services" className="hover:text-sky-400 transition-colors">AI Automation</a></li>
              <li><a href="#services" className="hover:text-sky-400 transition-colors">Content Marketing</a></li>
              <li><Link to="/cd-ielts" className="hover:text-sky-400 transition-colors">CD IELTS Solution</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-slate-400">
              <li>
                <a href="mailto:info@virtualswift.com" className="flex items-center gap-2 hover:text-sky-400 transition-colors">
                  <Mail className="w-4 h-4" />
                  info@virtualswift.com
                </a>
              </li>
              <li>
                <a href="tel:01346133117" className="flex items-center gap-2 hover:text-sky-400 transition-colors">
                  <Phone className="w-4 h-4" />
                  01346133117
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/virtualswift1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-sky-400 transition-colors">
                  <Facebook className="w-4 h-4" />
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} VirtualSwift. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppFloatingButton = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent('Hi, I am interested in your services.')}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full shadow-lg shadow-green-500/30 hover:shadow-xl transition-all hover:scale-105"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="font-medium hidden sm:block">Live Chat</span>
    </a>
  );
};

// Main Homepage
const HomePage = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Disable right-click and dev tools
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    const handleKeyDown = (e) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())) || (e.ctrlKey && e.key.toUpperCase() === 'U')) {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <HeroSection onOpenContactModal={() => setContactModalOpen(true)} />
      <ServicesSection />
      <AboutSection />
      <TrustedAgencySection />
      <ContactSection />
      <Footer />
      <WhatsAppFloatingButton />
      <ContactModal open={contactModalOpen} onOpenChange={setContactModalOpen} />
    </div>
  );
};

export default HomePage;
